import { brl, initials } from '../../lib/format'
import { color, radius } from '../../theme/tokens'
import type { Artifact, Case } from '../../lib/types'
import { ArtifactList } from './ArtifactList'
import { Check, Meter, PanelCard, Shield, Star, Warning } from '../ui'

const DOT_COLORS = {
  good: color.green,
  bad: color.red,
  warn: color.amber,
  neutral: color.blue,
} as const

const TITLE_COLORS = {
  good: color.greenInk,
  bad: color.redInk,
  warn: color.ink,
  neutral: color.ink,
} as const

/** Painel do devedor: dívida, scores, artefatos, histórico e garantias. */
export function CasePanel({ debtor, artifacts }: { debtor: Case; artifacts: Artifact[] }) {
  return (
    <aside
      className="dk"
      style={{
        width: 344,
        flex: 'none',
        background: color.surface,
        borderLeft: `1px solid ${color.border}`,
        overflowY: 'auto',
        padding: 14,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 11, marginBottom: 12 }}>
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: radius.xl,
            background: color.brandTint,
            border: `1px solid ${color.brandBorder}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 16,
            fontWeight: 700,
            color: color.brandInk,
            flex: 'none',
          }}
        >
          {initials(debtor.name)}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: '-.2px' }}>{debtor.name}</div>
          <div
            style={{
              fontSize: 11.5,
              color: color.inkSubtle,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {debtor.document_masked} · {debtor.segment}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
        {debtor.identity_validated ? (
          <Badge bg={color.greenTint} fg={color.greenInk} icon={<Check size={12} />}>
            Identidade validada
          </Badge>
        ) : null}
        {debtor.vip ? (
          <Badge bg={color.amberTint} fg={color.amberDeep} icon={<Star size={12} />}>
            VIP
          </Badge>
        ) : null}
        {debtor.lgpd_ok ? (
          <Badge bg={color.purpleTint} fg={color.purpleInk} icon={<Shield size={12} />}>
            LGPD OK
          </Badge>
        ) : null}
        {debtor.legal_process_active ? (
          <Badge bg={color.redTint} fg={color.redDeep} icon={<Warning size={12} />}>
            Processo jurídico ativo
          </Badge>
        ) : null}
      </div>

      <PanelCard title="DÍVIDA" style={{ marginBottom: 12 }}>
        <div style={{ padding: 12 }}>
          <div
            style={{
              fontSize: 24,
              fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
              letterSpacing: '-.4px',
            }}
          >
            {brl(debtor.total_debt)}
          </div>
          <div style={{ fontSize: 11.5, color: color.inkSubtle, marginBottom: 10 }}>
            saldo {brl(debtor.principal)} + encargos ·{' '}
            <b style={{ color: color.red }}>{debtor.days_overdue} dias em atraso</b>
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 5,
              fontSize: 11.5,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {debtor.charges.map((charge) => (
              <div key={charge.label} style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: color.inkSubtle }}>{charge.label}</span>
                <span style={{ fontWeight: 500 }}>{brl(charge.value)}</span>
              </div>
            ))}
          </div>
        </div>
      </PanelCard>

      <PanelCard title="SCORES" style={{ marginBottom: 12 }}>
        <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 11 }}>
          <ScoreRow
            label="Probabilidade de pagamento"
            value={debtor.scores.payment_probability}
            tint={color.green}
          />
          <ScoreRow
            label="Score de recuperação"
            value={debtor.scores.recovery_score}
            tint={color.amber}
          />
          <ScoreRow
            label="Cumprimento de promessa"
            value={debtor.scores.promise_compliance}
            tint={color.blue}
          />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingTop: 9,
              borderTop: `1px dashed ${color.border}`,
            }}
          >
            <span style={{ fontSize: 11.5, fontWeight: 600 }}>Risco de quebra de acordo</span>
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                fontSize: 11,
                fontWeight: 700,
                padding: '4px 9px',
                borderRadius: radius.pill,
                background: color.redTint,
                color: color.redInk,
              }}
            >
              <Warning size={12} />
              {debtor.scores.break_risk}
            </span>
          </div>
        </div>
      </PanelCard>

      <ArtifactList artifacts={artifacts} />

      <PanelCard title="HISTÓRICO">
        <div style={{ padding: '12px 12px 4px' }}>
          {debtor.history.map((event, index) => (
            <div key={index} style={{ display: 'flex', gap: 10, paddingBottom: 12 }}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  flex: 'none',
                  width: 10,
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: DOT_COLORS[event.dot],
                    flex: 'none',
                    marginTop: 4,
                  }}
                />
                <span
                  style={{ flex: 1, width: 1, background: color.border, marginTop: 3 }}
                />
              </div>
              <div style={{ minWidth: 0 }}>
                <div
                  style={{ fontSize: 11.5, fontWeight: 600, color: TITLE_COLORS[event.dot] }}
                >
                  {event.title}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: color.inkSubtle,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {event.date} {event.note}
                </div>
              </div>
            </div>
          ))}
        </div>
      </PanelCard>

      {debtor.collateral.length > 0 ? (
        <PanelCard title="GARANTIAS" style={{ marginTop: 12 }}>
          <div
            style={{
              padding: 12,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              fontSize: 11.5,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {debtor.collateral.map((item) => (
              <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>{item.label}</span>
                <span style={{ fontWeight: 600 }}>{brl(item.value)}</span>
              </div>
            ))}
            <div
              style={{
                fontSize: 11,
                color: color.inkSubtle,
                paddingTop: 6,
                borderTop: `1px dashed ${color.border}`,
              }}
            >
              Não exposto ao agente de voz (minimização de dados)
            </div>
          </div>
        </PanelCard>
      ) : null}
    </aside>
  )
}

function Badge({
  children,
  bg,
  fg,
  icon,
}: {
  children: React.ReactNode
  bg: string
  fg: string
  icon: React.ReactNode
}) {
  return (
    <span
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        fontSize: 11,
        fontWeight: 600,
        padding: '4px 9px',
        borderRadius: radius.pill,
        background: bg,
        color: fg,
      }}
    >
      {icon}
      {children}
    </span>
  )
}

function ScoreRow({ label, value, tint }: { label: string; value: number; tint: string }) {
  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: 11.5,
          fontWeight: 600,
          marginBottom: 5,
        }}
      >
        <span>{label}</span>
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}%</span>
      </div>
      <Meter value={value} tint={tint} />
    </div>
  )
}
